import { inject, observer } from 'mobx-react'
import { css } from '@emotion/react'
import posed, { PoseGroup } from 'react-pose'
import { Header, Page } from '../elements'
import { withRouterProps } from '../../../utils'
import { HelpIcon, TooltipText, PageLoading } from '../../../components'
import { useEffect, useState } from 'react'
import { API, API_URL, domain } from '../../../api'
import _ from 'lodash'
import LearnMoreDrawer from './learn-more/LearnMoreDrawer'
import {
  formatAmount,
  internalFreeTrialTerms,
  intervalAbbrev,
  isFreeTrial30Day,
  isInternalFreeTrial,
  priceSubtext,
  showsAsterisk,
  showsInterval,
} from './pricebook-display'

const RouteContainer = posed.div({
  enter: { opacity: 1, delay: 300, beforeChildren: true },
  exit: { opacity: 0 },
})

function RetailWelcome(props) {
  const {
    location,
    navigate,
    store: { selectPricebook, get3nDomain },
  } = props
  const [checkingAccess, setCheckingAccess] = useState(true)
  const [allPricebooks, setAllPricebooks] = useState([]) // includes base and add-on pricebooks
  const [pricebookOptions, setPricebookOptions] = useState([]) // only base pricebooks; add-ons aren't offered during sign up
  const [organizationData, setOrganizationData] = useState(undefined)
  const [signupPageText, setSignupPageText] = useState(undefined)
  const [learnMoreDrawerOpen, setLearnMoreDrawerOpen] = useState(false)
  const [learnMorePricebook, setLearnMorePricebook] = useState(undefined)
  const [termsOfServiceLink, setTermsOfServiceLink] = useState(null)
  // The Cancellation & Refund Policy applies to self-paid subscriptions. Derive it from the
  // actual mix of displayed offers — if any card is self-paid the link must show, even when the
  // set also contains an org-paid offer (an any(org-paid) flag would wrongly hide it).
  const hasSelfPaidPlan = pricebookOptions?.some(pb => pb.pricebook.paidBy !== 'ORG')
  const promotion = pricebookOptions?.filter(bundle =>
    bundle.prices.find(price => price.introductionPeriodInDays > 1)
  )
  const introPeriod = promotion
    ?.map(bundle => bundle.prices)
    .map(price => price.map(arr => arr.introductionPeriodInDays).find(promo => promo > 1))
  const introPeriodInMonths = introPeriod?.map(promo => Math.floor((promo ?? 0) / 30))
  const specialPromoOffer = pricebookOptions?.find(
    p => p.pricebook.metadata?.subHeader === 'Special Promo Offer!*'
  )
  const specialNonProfitOffer = pricebookOptions?.find(p =>
    p.pricebook.metadata?.subHeader?.includes('Special Non-Profit Offer')
  )
  const freemiumPricebook = pricebookOptions?.find(p => p.pricebook.metadata?.freemium)
  // The 32-day retail trial owns the free-trial footnote; the legacy 30-day trial states its
  // renewal terms on the card itself (see pricebook-display).
  const internalFreeTrialPricebook = pricebookOptions?.find(isInternalFreeTrial)
  const addOnPricebook = allPricebooks?.find(p => p.pricebook.addOn)

  // Redirect signed-in members without an active license to the Home upsell.
  useEffect(() => {
    let active = true

    const redirectIfUnlicensed = async () => {
      try {
        // Check authTracker directly because retail routes bypass AuthCheck.
        const { data: tracker } = await API.get('authTracker')

        if (tracker?.accountId) {
          const { data: license } = await API.get('license/status')

          if (active && license?.hasActiveLicense === false) {
            navigate('/home', { replace: true })
            return
          }
        }
      } catch (err) {
        // Show signup if the check fails; the backend still protects paid access.
        console.error(err)
      }

      if (active) setCheckingAccess(false)
    }

    redirectIfUnlicensed()

    return () => {
      active = false
    }
  }, [navigate])

  useEffect(() => {
    const getPricebookOptions = async () => {
      const { data } = await API.get('pricebook/available')
      const availableProductsData = [...data].filter(product => !product.pricebook.addOn)
      setAllPricebooks([...data])
      setPricebookOptions(availableProductsData)
    }
    getPricebookOptions()
  }, [])

  useEffect(() => {
    const getOrganizationData = async () => {
      const { data } = await API.get('organization')
      setOrganizationData(data)
    }
    getOrganizationData()
  }, [])

  // The organization's custom sign-up blurb. Optional decoration — if the org has no blurb
  // (or the call fails) the screen simply renders without it.
  useEffect(() => {
    const getOrganizationMetadata = async () => {
      try {
        const { data } = await API.get('organization-metadata')
        setSignupPageText(data?.signupPageText)
      } catch {
        setSignupPageText(undefined)
      }
    }
    getOrganizationMetadata()
  }, [])

  useEffect(() => {
    const getTermsLink = async () => {
      const res = await API.get('/termsAndConditions/docs/default')
      const termsObject = _.find(res.data, doc => {
        return doc.docDescription === 'Terms of Service'
      })
      const url = `${API_URL}termsAndConditions/default?docId=${termsObject.id}`
      setTermsOfServiceLink(url)
    }
    getTermsLink()
  }, [])

  // Choosing a plan begins the sign-up flow directly (no separate select + sign-up step).
  const onChoosePlan = async pricebook => {
    if (pricebook.pricebook.productType === 'GUIDED_CHOICE') {
      await selectPricebook(pricebook.pricebook.id)
      window.location.href = `https://retail-auth.${domain}/oauth2/authorization/B2C_1_signup1`
    } else {
      const domain3n = await get3nDomain()
      window.location.href = `https://app.${domain3n}/create-account?pricebookId=${pricebook.pricebook.id}`
    }
  }

  const termsLink = (
    <Page.Link href={termsOfServiceLink} target='_blank'>
      Terms of Service
    </Page.Link>
  )
  const cancellationLink = (
    <Page.Link href='/retail/payment/cancellation-policy' target='_blank'>
      Cancellation and Refund Policy
    </Page.Link>
  )
  // Show the cancellation policy whenever a self-paid plan is on offer (org-paid plans have no
  // user subscription to cancel); a mixed set still needs it for the self-paid card.
  const tosAndMaybeCancellation = (
    <>
      Please see our {termsLink} {hasSelfPaidPlan && <>and {cancellationLink} </>}for details.
    </>
  )
  // User-paid scenarios (free trial, promo): the cancellation policy always applies.
  const tosAndCancellation = (
    <>
      Please see our {termsLink} and {cancellationLink} for details.
    </>
  )

  // Hide signup until the check finishes so it doesn't flash before a redirect.
  if (checkingAccess) {
    return <PageLoading />
  }

  return (
    <PoseGroup animateOnMount>
      <RouteContainer key={location.key || location.pathname}>
        <LearnMoreDrawer
          pricebookBundle={learnMorePricebook}
          drawerOpen={learnMoreDrawerOpen}
          setDrawerOpen={setLearnMoreDrawerOpen}
        />
        <Page.Container>
          <Header
            organizationLogo={
              organizationData?.logoUri ? `${API_URL}/organization/logo` : undefined
            }
          />
          <Page.WelcomeContent>
            <Page.WelcomeTitle>Welcome! Choose your plan</Page.WelcomeTitle>
            {signupPageText?.trim() && <Page.WelcomeBlurb>{signupPageText}</Page.WelcomeBlurb>}
            <Page.LoginPrompt>
              Already have an account?{' '}
              <Page.Link
                href={`https://retail-auth.${domain}`}
                css={css`
                  text-decoration: underline;
                `}>
                Log in
              </Page.Link>
            </Page.LoginPrompt>
            <div
              css={css`
                padding: 25px;
                @media (max-width: 800px) {
                  padding: 30px;
                }
              `}
            />
            <ProductOptions
              pricebookOptions={pricebookOptions}
              onChoosePlan={onChoosePlan}
              setDrawerOpen={setLearnMoreDrawerOpen}
              setLearnMorePricebook={setLearnMorePricebook}
            />
            <Page.FootnoteGroup>
              {/* Mutually exclusive lead footnote: free trial > freemium > default.
                  Mirrors 3Nickels ChooseYourPlanSection / Access3NickelsRow. */}
              {internalFreeTrialPricebook ? (
                <div>
                  * {internalFreeTrialTerms(internalFreeTrialPricebook)} {tosAndCancellation}
                </div>
              ) : freemiumPricebook ? (
                <>
                  <div>
                    * Premium Features package available for in-app purchase.{' '}
                    <HelpIcon
                      tooltip={TooltipText.nickelsAddOnPremiumFeatures(
                        (addOnPricebook?.prices[0]?.price ?? 0) / 100,
                        addOnPricebook?.prices[0]?.intervalType
                      )}
                    />
                  </div>
                  <div>{tosAndMaybeCancellation}</div>
                </>
              ) : (
                !specialPromoOffer && !specialNonProfitOffer && <div>{tosAndMaybeCancellation}</div>
              )}
              {/* Additive: introductory free-access auto-renew notice. */}
              {(introPeriodInMonths?.length ?? 0) > 1 ? (
                <div>
                  *{freemiumPricebook ? '*' : ''} After the period of free access ends, Advice
                  subscription auto-renews at $14.99/month
                </div>
              ) : (
                introPeriodInMonths?.map((x, index) => (
                  <div key={index}>
                    * After {x === 1 ? '30 days' : `${x} months`}, Advice subscription auto-renews
                    at $14.99/month
                  </div>
                ))
              )}
              {/* Additive: promotional / non-profit offer terms. */}
              {(specialPromoOffer || specialNonProfitOffer) && (
                <div>
                  * This subscription automatically renews every month and will continue to
                  auto-renew until you cancel. Terms and conditions apply. Promotional pricing
                  discount is off standard monthly pricing. We reserve the right to validate
                  eligibility and to modify or end this promotional offer at any time.{' '}
                  {tosAndCancellation}
                </div>
              )}
            </Page.FootnoteGroup>

            <Page.ContactBlock />

            {/* GC logo at the bottom, centered — mobile only */}
            <Page.BottomLogo />
          </Page.WelcomeContent>
        </Page.Container>
      </RouteContainer>
    </PoseGroup>
  )
}

export const ProductOptions = props => {
  const { pricebookOptions, onChoosePlan, setDrawerOpen, setLearnMorePricebook } = props

  const shortDescription = pb => {
    const { productType, pricebookName, metadata } = pb.pricebook
    if (metadata?.shortDescription) return metadata.shortDescription
    if (productType === 'GUIDED_CHOICE')
      return 'Retirement planning: saving for and spending during'
    if (pricebookName === '3Nickels Advice') return 'Expert advice on your whole financial picture.'
    return pb.pricebook.description?.[0] ?? ''
  }

  // Small gray line under the title, e.g. the promo sub-header or "32 Days Free".
  const smallNote = pb => pb.pricebook.metadata?.subHeader ?? null

  const ctaLabel = pb => {
    if (isInternalFreeTrial(pb) || isFreeTrial30Day(pb)) return 'Start free trial'
    return pb.prices[0].price > 0 ? 'Choose & get access' : 'Choose & claim access'
  }

  return (
    <Page.PlanRow>
      {pricebookOptions.map((pb, index) => {
        const orgPays = pb.pricebook.paidBy === 'ORG'
        const price = pb.prices[0]
        const showAsterisk = showsAsterisk(pb)
        const note = smallNote(pb)
        const pNote = priceSubtext(pb)
        return (
          <Page.PlanCard key={pb.pricebook?.id ?? index}>
            <Page.PlanArt src={pb.pricebook.artUrl} alt={`${pb.pricebook.pricebookName} art`} />
            <Page.PlanTitle>{pb.pricebook.pricebookName}</Page.PlanTitle>
            {note && <Page.PlanSmallNote>{note}</Page.PlanSmallNote>}
            <Page.PlanDesc>{shortDescription(pb)}</Page.PlanDesc>
            <Page.PlanLearnMore
              onClick={() => {
                setLearnMorePricebook(pb)
                setDrawerOpen(true)
              }}>
              Learn more
            </Page.PlanLearnMore>
            <Page.PlanFooter>
              {orgPays ? (
                <Page.PlanOrgPays>
                  paid by your organization{showAsterisk ? '*' : ''}
                </Page.PlanOrgPays>
              ) : (
                <Page.PlanPrice>
                  {isFreeTrial30Day(pb) ? (
                    <span className='amount'>30 days free</span>
                  ) : (
                    <>
                      <span className='currency'>$</span>
                      <span className='amount'>{formatAmount(price.price / 100)}</span>
                      {showsInterval(pb) && (
                        <span className='interval'>/{intervalAbbrev[price.intervalType]}</span>
                      )}
                      {showAsterisk && <span className='asterisk'>*</span>}
                    </>
                  )}
                </Page.PlanPrice>
              )}
              {!orgPays && pNote && <Page.PlanPriceNote>{pNote}</Page.PlanPriceNote>}
              {onChoosePlan && (
                <Page.PlanCta onClick={() => onChoosePlan(pb)}>{ctaLabel(pb)}</Page.PlanCta>
              )}
            </Page.PlanFooter>
          </Page.PlanCard>
        )
      })}
    </Page.PlanRow>
  )
}

export default withRouterProps(inject('store')(observer(RetailWelcome)))
